// src/hooks/useOrders.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { OrderService } from '../admin/pages/order/orderService';

export const ORDER_KEYS = {
  all:       ['orders'] as const,
  lists:     () => ['orders', 'list'] as const,
  paginated: (
    page: number,
    search: string,
    status: string,
    paymentStatus: string,
    warehouse: string,
    dateFrom: string,
    dateTo: string
  ) => ['orders', 'list', page, search, status, paymentStatus, warehouse, dateFrom, dateTo] as const,
  stats:     () => ['orders', 'stats'] as const,
};

// FIX: Removed the `cursors` field — seek-based pagination only needs `page`.
export interface OrderPaginationParams {
  page: number;
  search: string;
  statusFilter: string;
  paymentStatusFilter: string;
  warehouseFilter: string;
  dateFromFilter: string;
  dateToFilter: string;
  pageSize: number;
  // `cursors` intentionally removed — seek-based pagination uses only `page`
}

// FIX: Passes `params.page` directly to getOrdersPaginated instead of
// cursor objects. Any page can now be fetched in any order.
export const useOrdersPaginated = (params: OrderPaginationParams) => {
  return useQuery({
    queryKey: ORDER_KEYS.paginated(
      params.page,
      params.search,
      params.statusFilter,
      params.paymentStatusFilter,
      params.warehouseFilter,
      params.dateFromFilter,
      params.dateToFilter,
    ),
    queryFn: () =>
      OrderService.getOrdersPaginated(
        params.pageSize,
        params.page,
        params.search        || undefined,
        params.statusFilter        !== 'all' ? params.statusFilter        : undefined,
        params.paymentStatusFilter !== 'all' ? params.paymentStatusFilter : undefined,
        params.warehouseFilter     !== 'all' ? params.warehouseFilter     : undefined,
        params.dateFromFilter      || undefined,
        params.dateToFilter        || undefined,
      ),
    staleTime: 1000 * 60 * 2,
    placeholderData: (prev: any) => prev,
  });
};

export const useOrderStats = () => {
  return useQuery({
    queryKey: ORDER_KEYS.stats(),
    queryFn: () => OrderService.getOrderStats(),
    staleTime: 1000 * 60 * 3,
  });
};

export const useOrderMutations = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ORDER_KEYS.all });
  };

  const remove = useMutation({
    mutationFn: (orderId: string) => OrderService.deleteOrder(orderId),
    onSuccess: invalidateAll,
  });

  return { remove };
};